/**
 * Build-time configuration. Values come from `.env.<mode>` (staging or production), so the backend is baked into
 * the bundle and cannot be switched at runtime.
 */
export interface WebConfig {
  readonly environment: 'staging' | 'production';
  readonly supabaseUrl: string;
  readonly supabasePublishableKey: string;
  /** Tenant for localhost and `*.pages.dev` when the URL has no `?tenant=`. */
  readonly defaultTenant: string;
  /** `<slug>.<root domain>` resolves to tenant `<slug>`. */
  readonly rootDomains: readonly string[];
  /**
   * Custom portal hostnames mapped to tenant slugs. The first host listed for a slug is that company's main address,
   * where the front door sends its people (docs/specs/cloud/front-door.md §2.2).
   */
  readonly customDomains: ReadonlyMap<string, string>;
  /** Hosts that show Teslyn's own sign-in, the front door, e.g. `cloud.teslyn.au` (decision 699). */
  readonly frontDoorHosts?: readonly string[];
  /** Hosts that send the browser on to another host, same path and query, e.g. `cloud.teslyn.com.au` (decision 681). */
  readonly forwardHosts?: ReadonlyMap<string, string>;
  /**
   * The cookie domain of the shared sign-in (decision 978; docs/specs/cloud/shared-sign-in.md), e.g. `cloud.teslyn.au`:
   * every `https` address under it keeps one session. Empty: each address keeps its own (localStorage).
   */
  readonly authCookieDomain?: string;
  /** Feature flags for OAuth providers; off shows the button disabled with "Available soon". */
  readonly auth: {
    readonly google: boolean;
    readonly microsoft: boolean;
    /** Sign in with LinkedIn (decision 804; Supabase's `linkedin_oidc`). Off: "Available soon". */
    readonly linkedin?: boolean;
    /**
     * The portal addresses (origins) the Supabase project's passkey settings list (decision 804): passkeys work only
     * on an address whose host is the relying party's domain or under it and that is in that list. Empty: no passkeys.
     */
    readonly passkeyOrigins?: readonly string[];
  };
}

function list(v: string | undefined): string[] {
  return (v ?? '')
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
}

export function parseCustomDomains(v: string | undefined): Map<string, string> {
  const out = new Map<string, string>();
  for (const pair of list(v)) {
    const [host, slug] = pair.split('=').map((s) => s.trim());
    if (host && slug) out.set(host, slug);
  }
  return out;
}

const on = (v: string | undefined): boolean => v === 'on' || v === 'true';

export function readConfig(env: ImportMetaEnv = import.meta.env): WebConfig {
  return {
    environment: env.VITE_TESLYN_ENVIRONMENT === 'production' ? 'production' : 'staging',
    supabaseUrl: env.VITE_SUPABASE_URL ?? '',
    supabasePublishableKey: env.VITE_SUPABASE_PUBLISHABLE_KEY ?? '',
    defaultTenant: env.VITE_DEFAULT_TENANT ?? '',
    rootDomains: list(env.VITE_ROOT_DOMAINS),
    customDomains: parseCustomDomains(env.VITE_CUSTOM_DOMAINS),
    frontDoorHosts: list(env.VITE_FRONT_DOOR_HOSTS),
    forwardHosts: parseCustomDomains(env.VITE_FORWARD_HOSTS),
    authCookieDomain: (env.VITE_AUTH_COOKIE_DOMAIN ?? '').trim().toLowerCase().replace(/^\.+/, ''),
    auth: {
      google: on(env.VITE_AUTH_GOOGLE),
      microsoft: on(env.VITE_AUTH_MICROSOFT),
      linkedin: on(env.VITE_AUTH_LINKEDIN),
      passkeyOrigins: list(env.VITE_AUTH_PASSKEY_ORIGINS).map((o) => o.replace(/\/+$/, '')),
    },
  };
}

/**
 * Whether passkeys can be offered on this address: listed in the build's passkey origins, and the browser has
 * WebAuthn. The Supabase project refuses a passkey from any other address, so the button would only fail there.
 */
export function passkeysHere(
  auth: WebConfig['auth'],
  win: { readonly location: { readonly origin: string }; readonly PublicKeyCredential?: unknown } = window,
): boolean {
  const origin = win.location.origin.toLowerCase();
  return (auth.passkeyOrigins ?? []).includes(origin) && typeof win.PublicKeyCredential !== 'undefined';
}
